import { z } from "zod";

import { agentOutputSchema } from "./agent-contract.js";

/**
 * The event vocabulary that flows through the agent → orchestrator SSE
 * stream introduced for blocking dispatch (issue
 * [#229](https://github.com/PROSPECT-TECH-LTD/prospect-platform/issues/229)).
 *
 * Worker agents on Cloud Run respond to `POST /run` with a
 * long-running `text/event-stream` body instead of the historical
 * 202+callback. Each event the agent emits is one SSE message with
 * `event:` set to the union discriminator (`mode_progress`,
 * `run_complete`, …) and `data:` set to the JSON-encoded payload.
 *
 * The orchestrator's dispatch path parses these on the way in and
 * republishes the interesting ones to {@link RunEventBus} so admin-UI
 * subscribers see live progress without the agent ever talking to the
 * existing `POST /api/callbacks/:jobId` route. The terminal event is
 * `run_complete`, whose payload is the same `AgentOutput` shape the
 * legacy callback would have POSTed — that lets the orchestrator's
 * dispatch path invoke the same {@link applyAgentOutput} helper the
 * callback route uses, with one source of transition logic.
 *
 * **Versioning.** The union is open: new event types can be added
 * without breaking subscribers because `runStreamEventSchema` is a
 * Zod discriminated union and `parseRunStreamEvent` returns `null` on
 * unknown discriminators. Subscribers must therefore tolerate unknown
 * events (ignore them) rather than treating them as errors.
 *
 * **Scope today.** Only `mode_progress` (load-bearing for issue
 * [#217](https://github.com/PROSPECT-TECH-LTD/prospect-platform/issues/217)'s
 * live mode-progress panel) and `run_complete` (required AC for
 * issue #229) ship in the first PR. Per-LLM-call and pacer-wait
 * events can be added incrementally; the admin UI's existing
 * `run_log` poll already surfaces per-call visibility, so they are
 * not blocking.
 */

export const modeProgressEventSchema = z.object({
  type: z.literal("mode_progress"),
  jobId: z.string().uuid(),
  /**
   * The curator's discovery mode identifier — `mode_1`, `mode_2`, or
   * `mode_3` today, free text in the schema so a future agent with a
   * different stage vocabulary doesn't have to extend this enum.
   */
  mode: z.string().min(1),
  /**
   * Stage state at the moment the LLM called the
   * `record_mode_progress` tool. The curator only reports
   * `running` (entering a mode) and `complete` (finishing one); the
   * `queued` UI state is inferred client-side from "modes not yet
   * mentioned in any event."
   */
  status: z.enum(["running", "complete"]),
  /** ISO-8601 wall-clock stamp from the curator. */
  at: z.string().min(1),
});

export type ModeProgressEvent = z.infer<typeof modeProgressEventSchema>;

export const runCompleteEventSchema = z.object({
  type: z.literal("run_complete"),
  jobId: z.string().uuid(),
  /**
   * The agent's final outcome — same shape the legacy callback handler
   * accepted. The orchestrator hands this payload directly to the
   * shared `applyAgentOutput` helper, which performs the state
   * transition, audit, bus publish, and validator-hop dispatch.
   *
   * The agent MUST write the same payload to `jobs.worker_result`
   * BEFORE emitting this event so a mid-stream connection drop can be
   * recovered by reading the DB column. See `docs/agent-dispatch.md`
   * for the durability contract.
   */
  output: agentOutputSchema,
});

export type RunCompleteEvent = z.infer<typeof runCompleteEventSchema>;

/**
 * Periodic liveness signal from a worker's SSE route handler
 * (issue [#655](https://github.com/PROSPECT-TECH-LTD/prospect-platform/issues/655)).
 *
 * The orchestrator's stream consumers explicitly skip this event
 * — it never reaches `applyAgentOutput` or the event bus. Its only
 * purpose is to reset `withIdleTimeout`'s timer by virtue of
 * yielding through `parseSseStream`. See
 * `packages/shared/src/sse-heartbeat.ts` for the emitter and
 * `docs/agent-dispatch.md` § `heartbeat` for the wire contract.
 *
 * Why not SSE comments? The previous keepalive pushed
 * `{ data: "", comment: "keepalive" }`, which writes `:keepalive`
 * lines on the wire. Those don't complete an SSE event, so
 * `parseSseStream` never yields for them, so the idle-timeout
 * guard never saw them and the timer ran down. A typed event
 * resolves that.
 */
export const heartbeatEventSchema = z.object({
  type: z.literal("heartbeat"),
  jobId: z.string().uuid(),
  /** ISO-8601 wall-clock stamp at emit time. */
  at: z.string().min(1),
});

export type HeartbeatEvent = z.infer<typeof heartbeatEventSchema>;

export const runStreamEventSchema = z.discriminatedUnion("type", [
  modeProgressEventSchema,
  runCompleteEventSchema,
  heartbeatEventSchema,
]);

/**
 * One event in the agent → orchestrator SSE stream. The agent emits
 * exactly one `run_complete` event at the end of every run (success
 * or failure); intermediate events are optional progress signals.
 */
export type RunStreamEvent = z.infer<typeof runStreamEventSchema>;

/**
 * Parse a JSON-decoded SSE payload into a typed `RunStreamEvent`.
 * Returns `null` on unknown event types or schema mismatches — the
 * orchestrator forwards what it understands and drops the rest, so a
 * future agent can introduce a new event type without breaking an
 * older orchestrator.
 *
 * The SSE wire framing (`event:` line + `data:` line + blank line) is
 * the consumer's responsibility; this helper takes the parsed JSON
 * body and applies the schema.
 */
export function parseRunStreamEvent(value: unknown): RunStreamEvent | null {
  const result = runStreamEventSchema.safeParse(value);
  return result.success ? result.data : null;
}
